import { Icon, OspModal, ospThemes } from "@ospweb/sdk-ui";
import { cn, formatAddress } from "utils";
import { useEffect, useState } from "react";

import { ShellLink } from "@/src/components";
import { I18nUtil } from "@utils/i18n";
import { ROUTER_PATH } from "@/src/config";
import Translations from "@translate/components/Setting/WalletConfirmModal";

const I18n = I18nUtil.createScoped(Translations);

type WalletInfo = {
  chain_name: string;
  platform_name: string;
  platform_id: string;
  bind_time: number;
};

/**
 * Get wallet identifier based on chain type
 * For SOLANA chain, use platform_name; otherwise use platform_id
 * @param wallet - Wallet information object
 * @returns Wallet identifier string
 */
const getWalletId = (wallet: WalletInfo | undefined): string => {
  if (!wallet) {
    return "";
  }
  return wallet.chain_name === "SOLANA"
    ? wallet.platform_name || ""
    : wallet.platform_id || "";
};

type WalletConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  wallets: WalletInfo[];
  onConfirm: (walletAddress: string) => void;
  onAddWallet: () => void;
  isLoading?: boolean;
};

const Radio = ({
  selected,
  disabled,
}: {
  selected: boolean;
  disabled: boolean;
}) => {
  return (
    <div
      className={cn(
        "w-4 h-4 rounded-full bg-background_white_overlay10 border-2 border-transparent hover:border-border_brand_transparent50 relative group-hover:border-border_brand_transparent50",
        selected &&
          "border-border_brand_solid border-4 border-border_brand_solid before:block before:content-[''] before:w-2 before:h-2 before:rounded-full before:bg-background_inverse_overlay100 before:absolute before:left-0 before:top-0",
        disabled &&
          "bg-background_white_overlay5 before:bg-background_white_overlay20",
      )}
    />
  );
};

export const WalletConfirmModal = ({
  open,
  onClose,
  wallets,
  onConfirm,
  onAddWallet,
  isLoading,
}: WalletConfirmModalProps) => {
  const [selectedWallet, setSelectedWallet] = useState<string>("");

  // Auto-select the first wallet if there's only one wallet when modal opens
  useEffect(() => {
    if (open && wallets?.length >= 1) {
      // Always auto-select when there's only one wallet
      const firstWalletId = getWalletId(wallets[0]);
      setSelectedWallet(firstWalletId);
    }
    // Reset selection when modal closes
    if (!open) {
      setSelectedWallet("");
    }
  }, [open, wallets]);

  return (
    <OspModal
      open={open}
      onClose={onClose}
      footer={false}
      titleStyle={{
        minHeight: 0,
      }}
      containerClassName="!md:w-[400px] !md:rounded-[16px]"
      contentStyle={{
        backgroundColor: ospThemes.colors.background_solid_secondary,
        padding: 0,
        borderRadius: "16px",
      }}
      showClose={false}
      centered
      destroyOnClose
    >
      <div className="p-6 bg-background_solid_secondary flex flex-col gap-6 relative">
        <Icon
          data-openreplay-label="close"
          name="close"
          size={16}
          className="button_icon p-[6px] text-content_tertiary absolute top-3 right-3 cursor-pointer"
          onClick={onClose}
        />
        <div className="flex flex-col gap-2">
          <div className="headline_regular_s text-content_primary">
            {I18n.t("Wallet Confirmation")}
          </div>
          <div className="body_m text-content_secondary">
            {I18n.t(
              "Connect the wallet that has completed the task for verification",
            )}
          </div>
        </div>
        <div className="flex flex-col gap-3 w-full">
          {wallets?.map((wallet, index) => {
            const walletId = getWalletId(wallet);
            const isActive = selectedWallet === walletId;
            return (
              <div
                data-openreplay-label="Setting · WalletConfirmModal · div"
                key={index}
                onClick={() => {
                  setSelectedWallet(walletId);
                }}
                className="flex items-center gap-3 p-4 rounded-lg border border-background_white_overlay10 cursor-pointer w-full group"
              >
                <Radio selected={isActive} disabled={false} />
                <div
                  className="text-content_primary body_m flex items-center gap-2"
                  style={{
                    color:
                      index === 0
                        ? ospThemes.colors.content_primary
                        : ospThemes.colors.content_secondary,
                  }}
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-background_white_overlay10">
                    <Icon
                      name="wallet"
                      size={12}
                      className="text-content_primary"
                    />
                  </div>
                  <div className="body_medium_m text-content_primary">
                    {formatAddress(wallet?.platform_id, 4)}
                  </div>
                </div>
              </div>
            );
          })}
          {wallets?.length < 6 && (
            <button
              aria-label="Wallet Confirm Modal"
              data-openreplay-label="Wallet Confirm Modal Button"
              id="btn-wallet-confirm-modal"
              className="h-14 flex items-center justify-center rounded-lg text-content_primary button_m gap-1 border border-dashed border-background_white_overlay20 hover:text-content_secondary"
              onClick={() => {
                setSelectedWallet("");
                onAddWallet();
              }}
            >
              <Icon name="plus" size={20} />
              {I18n.t("Add new wallet and verify")}
            </button>
          )}
        </div>
        <div
          className="body_s text-content_tertiary"
          style={{
            display: wallets?.length < 6 ? "none" : "block",
          }}
        >
          <span>
            {I18n.t(
              "The wallet binding has reached its limit. You can go to the",
            )}
          </span>
          <ShellLink
            aria-label="Wallet Confirm Modal"
            data-openreplay-label={`Wallet Confirm Modal Link | ${ROUTER_PATH.SETTING} | ${I18n.t("Settings")}`}
            id="lnk-wallet-confirm-modal"
            onClick={onClose}
            className="underline text-content_primary px-1"
            href={ROUTER_PATH.SETTING}
          >
            {I18n.t("Settings")}
          </ShellLink>
          <span>{I18n.t("to manage your wallet")}</span>
        </div>
        <button
          aria-label="Wallet Confirm Modal"
          data-openreplay-label="Wallet Confirm Modal Button"
          id="btn-wallet-confirm-modal-2"
          className="button_m button md_button primary_button gap-1 w-full group"
          onClick={() => {
            onConfirm(selectedWallet);
          }}
          disabled={isLoading}
          data-loading={isLoading}
        >
          <span className="group-loading:hidden text-content_inverse_primary">
            {I18n.t("Verify Now")}
          </span>
          <Icon
            name="ic_loading2"
            size={20}
            color={ospThemes.colors.content_inverse_primary}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
      </div>
    </OspModal>
  );
};
